import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { updateTranscript, fetchTranscript, fetchTranscriptLockStatus, updateTranscriptLockStatus } from '../GoogleSheetsService';
import './ClassTranscript.css';
import * as XLSX from 'xlsx';

const ClassTranscript = () => {
  const { blockId, classId } = useParams();
  const navigate = useNavigate();
  const { studentsData, classMap } = useContext(StudentContext);
  const userRole = localStorage.getItem('userRole') || 'catechist';
  const isAdmin = userRole === 'admin';

  const displayClass = classMap[classId] || 'Lớp Không Xác Định';
  const students = studentsData[classId] || [];

  const [isEditing, setIsEditing] = useState(false);
  const [transcript, setTranscript] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  const initializeTranscript = (fetchedTranscript) => {
    const defaultTranscript = students.reduce((acc, student) => {
      acc[student.id] = {
        semester1: {
          oral: '',
          fifteenMinutes: '',
          onePeriod: '',
          exam: '',
          average: '',
          diligence: '',
        },
        semester2: {
          oral: '',
          fifteenMinutes: '',
          onePeriod: '',
          exam: '',
          average: '',
          diligence: '',
        },
        yearly: {
          average: '',
          diligence: '',
          rank: '',
          oralExam: '',
        },
      };
      return acc;
    }, {});

    const mergedTranscript = { ...defaultTranscript, ...fetchedTranscript };
    Object.keys(mergedTranscript).forEach((studentId) => {
      const studentTranscript = mergedTranscript[studentId];
      studentTranscript.semester1 = studentTranscript.semester1 || {};
      studentTranscript.semester2 = studentTranscript.semester2 || {};
      studentTranscript.yearly = studentTranscript.yearly || {};

      studentTranscript.semester1.diligence = String(studentTranscript.semester1.diligence || '');
      studentTranscript.semester2.diligence = String(studentTranscript.semester2.diligence || '');
      studentTranscript.yearly.diligence = String(studentTranscript.yearly.diligence || '');
      studentTranscript.yearly.oralExam = String(studentTranscript.yearly.oralExam || '');
      ['semester1', 'semester2'].forEach((semester) => {
        studentTranscript[semester] = studentTranscript[semester] || {};
        ['oral', 'fifteenMinutes', 'onePeriod', 'exam', 'average'].forEach((field) => {
          studentTranscript[semester][field] = String(studentTranscript[semester][field] || '');
        });
      });
      studentTranscript.yearly.average = String(studentTranscript.yearly.average || '');
      studentTranscript.yearly.rank = String(studentTranscript.yearly.rank || '');
    });

    const rankMap = calculateRank(mergedTranscript);
    Object.keys(mergedTranscript).forEach((studentId) => {
      if (!mergedTranscript[studentId].yearly) {
        mergedTranscript[studentId].yearly = {};
      }
      mergedTranscript[studentId].yearly.rank = rankMap[studentId] || '';
    });

    return mergedTranscript;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [fetchedTranscript, lockStatus] = await Promise.all([
          fetchTranscript(classId),
          fetchTranscriptLockStatus(classId),
        ]);
        if (students.length > 0) {
          const initializedTranscript = initializeTranscript(fetchedTranscript);
          setTranscript(initializedTranscript);
        } else {
          console.warn("Student data not yet available or class empty during transcript initialization.");
          setTranscript(initializeTranscript(fetchedTranscript));
        }

        setIsLocked(lockStatus);
      } catch (error) {
        console.error('Error loading data:', error);
        if (students.length > 0) {
          const initializedTranscript = initializeTranscript({});
          setTranscript(initializedTranscript);
        } else {
          setTranscript({});
        }
        setIsLocked(false);
      } finally {
        setLoading(false);
      }
    };

    if (studentsData && classMap && classId && studentsData[classId]) {
      loadData();
    } else if (!loading && studentsData && classMap && classId && !studentsData[classId]) {
      console.log(`No students found for classId: ${classId}`);
      setTranscript(initializeTranscript({}));
      setLoading(false);
      fetchTranscriptLockStatus(classId)
        .then(setIsLocked)
        .catch(() => setIsLocked(false));
    }
  }, [classId, studentsData, classMap]);

  const calculateSemesterAverage = (scores) => {
    const weights = {
      oral: 1,
      fifteenMinutes: 1,
      onePeriod: 2,
      exam: 4,
    };

    const requiredFields = ['oral', 'fifteenMinutes', 'onePeriod', 'exam'];
    const currentScores = requiredFields.reduce((acc, field) => {
      acc[field] = scores[field] || '';
      return acc;
    }, {});

    const allFieldsValid = requiredFields.every((field) => {
      const score = currentScores[field];
      return score === '' || !isNaN(parseFloat(score));
    });

    const anyScoreEntered = requiredFields.some(field => {
      const score = currentScores[field];
      return score !== '' && !isNaN(parseFloat(score));
    });

    if (!anyScoreEntered) {
      return '';
    }

    const allRequiredPresentAndValid = requiredFields.every(field => {
      const score = currentScores[field];
      return score !== '' && !isNaN(parseFloat(score));
    });

    if (!allRequiredPresentAndValid) {
      return '';
    }

    let totalScore = 0;
    let totalWeight = 0;

    for (const [key, score] of Object.entries(currentScores)) {
      const scoreValue = parseFloat(score);
      const weight = weights[key];
      if (weight) {
        totalScore += scoreValue * weight;
        totalWeight += weight;
      }
    }

    if (totalWeight === 0) return '';
    const average = totalScore / totalWeight;
    return average.toFixed(2);
  };

  const calculateYearlyAverage = (sem1AvgStr, sem2AvgStr) => {
    const sem1Avg = parseFloat(sem1AvgStr);
    const sem2Avg = parseFloat(sem2AvgStr);

    const sem1IsValid = !isNaN(sem1Avg);
    const sem2IsValid = !isNaN(sem2Avg);

    if (sem1IsValid && sem2IsValid) {
      return ((sem1Avg + sem2Avg) / 2).toFixed(2);
    }
    return '';
  };

  const calculateRank = (currentTranscript) => {
    const rankMap = {};

    Object.keys(currentTranscript).forEach((studentId) => {
      rankMap[studentId] = '';
    });

    const studentsWithScores = Object.keys(currentTranscript)
      .map((studentId) => {
        const yearlyData = currentTranscript[studentId]?.yearly || {};
        const yearlyAvg = parseFloat(yearlyData.average) || 0;
        return {
          studentId,
          yearlyAvg,
          diligence: String(yearlyData.diligence || '').toLowerCase(),
        };
      })
      .filter(student => student.yearlyAvg > 0);

    if (studentsWithScores.length === 0) {
      return rankMap;
    }

    studentsWithScores.sort((a, b) => b.yearlyAvg - a.yearlyAvg);

    let currentRank = 1;
    let position = 1;
    let previousAvg = null;
    let studentsAtCurrentRank = 0;

    studentsWithScores.forEach((student, index) => {
      const { studentId, yearlyAvg, diligence } = student;

      if (diligence === 'kém' || yearlyAvg < 5.0) {
        rankMap[studentId] = 'Ở lại lớp';
        return;
      }

      if (previousAvg !== null && yearlyAvg !== previousAvg) {
        currentRank = position;
        studentsAtCurrentRank = 0;
      }

      if (currentRank === 1) {
        rankMap[studentId] = 'Hạng 1';
      } else if (currentRank === 2) {
        rankMap[studentId] = 'Hạng 2';
      } else if (currentRank === 3) {
        rankMap[studentId] = 'Hạng 3';
      } else {
        rankMap[studentId] = 'Được lên lớp';
      }

      studentsAtCurrentRank++;
      previousAvg = yearlyAvg;
      position++;
    });

    return rankMap;
  };

  const handleScoreChange = (studentId, semester, field, value) => {
    let correctedValue = value;
    let message = '';

    if (value.includes(',')) {
      correctedValue = value.replace(/,/g, '.');
      message = `Đã tự động đổi dấu phẩy (,) thành dấu chấm (.) cho ô điểm này.`;
    }

    const typingPattern = /^[0-9]*\.?[0-9]*$/;
    if (!typingPattern.test(correctedValue)) {
      setValidationMessage(`Điểm chỉ nên chứa số và dấu chấm (.).`);
    } else {
      const numValue = parseFloat(correctedValue);
      if (correctedValue !== '' && (numValue < 0 || numValue > 10)) {
        message = `Điểm phải nằm trong khoảng từ 0 đến 10.`;
      } else {
        if (!message) {
          setValidationMessage('');
        }
      }
    }

    if (message) {
      setValidationMessage(message);
      setTimeout(() => setValidationMessage(prev => prev === message ? '' : prev), 3000);
    }

    setTranscript((prev) => {
      const updatedStudent = JSON.parse(JSON.stringify(prev[studentId] || {
        semester1: {}, semester2: {}, yearly: {}
      }));

      if (semester === 'yearly' && field === 'oralExam') {
        updatedStudent.yearly.oralExam = correctedValue;
      } else {
        if (!updatedStudent[semester]) {
          updatedStudent[semester] = {};
        }
        updatedStudent[semester][field] = correctedValue;

        const sem1Scores = updatedStudent.semester1 || {};
        const sem2Scores = updatedStudent.semester2 || {};

        const sem1Avg = calculateSemesterAverage(sem1Scores);
        const sem2Avg = calculateSemesterAverage(sem2Scores);

        updatedStudent.semester1.average = sem1Avg;
        updatedStudent.semester2.average = sem2Avg;

        const yearlyAvg = calculateYearlyAverage(sem1Avg, sem2Avg);
        updatedStudent.yearly.average = yearlyAvg;
      }

      const updatedTranscript = {
        ...prev,
        [studentId]: updatedStudent,
      };

      const rankMap = calculateRank(updatedTranscript);
      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]) {
          if (!updatedTranscript[id].yearly) {
            updatedTranscript[id].yearly = {};
          }
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleDiligenceChange = (studentId, semester, value) => {
    setTranscript((prev) => {
      const updatedStudent = JSON.parse(JSON.stringify(prev[studentId] || {
        semester1: {}, semester2: {}, yearly: {}
      }));

      if (!updatedStudent[semester]) {
        updatedStudent[semester] = {};
      }

      updatedStudent[semester].diligence = value;

      const updatedTranscript = {
        ...prev,
        [studentId]: updatedStudent,
      };

      const rankMap = calculateRank(updatedTranscript);
      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]) {
          if (!updatedTranscript[id].yearly) {
            updatedTranscript[id].yearly = {};
          }
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleRecalculate = () => {
    setTranscript((prev) => {
      const updatedTranscript = JSON.parse(JSON.stringify(prev));
      
      Object.keys(updatedTranscript).forEach((studentId) => {
        const student = updatedTranscript[studentId];
        const sem1Scores = student.semester1 || {};
        const sem2Scores = student.semester2 || {};

        const sem1Avg = calculateSemesterAverage(sem1Scores);
        const sem2Avg = calculateSemesterAverage(sem2Scores);

        student.semester1.average = sem1Avg;
        student.semester2.average = sem2Avg;

        const yearlyAvg = calculateYearlyAverage(sem1Avg, sem2Avg);
        student.yearly.average = yearlyAvg;
      });

      const rankMap = calculateRank(updatedTranscript);
      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]) {
          if (!updatedTranscript[id].yearly) {
            updatedTranscript[id].yearly = {};
          }
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValidationMessage('');
    const loadData = async () => {
      try {
        setLoading(true);
        const fetchedTranscript = await fetchTranscript(classId);
        const initializedTranscript = initializeTranscript(fetchedTranscript);
        setTranscript(initializedTranscript);
      } catch (error) {
        console.error('Error reloading transcript:', error);
        setTranscript(initializeTranscript({}));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  };

  const handleEdit = () => {
    if (isLocked) {
      setModalMessage('Bảng điểm đã bị khóa. Vui lòng mở khóa trước khi sửa.');
      setModalType('error');
      setShowModal(true);
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    let hasInvalidScores = false;
    let invalidMessage = 'Vui lòng kiểm tra lại các ô điểm. Điểm phải từ 0 đến 10 và dùng dấu chấm (.) cho thập phân.';
    for (const studentId in transcript) {
      for (const semester of ['semester1', 'semester2']) {
        for (const field of ['oral', 'fifteenMinutes', 'onePeriod', 'exam']) {
          const score = transcript[studentId]?.[semester]?.[field];
          if (score && score !== '') {
            const numValue = parseFloat(score);
            if (isNaN(numValue) || numValue < 0 || numValue > 10) {
              hasInvalidScores = true;
              console.error(`Invalid score found before save: Student ${studentId}, ${semester}, ${field} = ${score}`);
              break;
            }
          }
        }
        if (hasInvalidScores) break;
      }
      const oralExamScore = transcript[studentId]?.yearly?.oralExam;
      if (oralExamScore && oralExamScore !== '') {
        const numValue = parseFloat(oralExamScore);
        if (isNaN(numValue) || numValue < 0 || numValue > 10) {
          hasInvalidScores = true;
          console.error(`Invalid oralExam score found before save: Student ${studentId}, yearly, oralExam = ${oralExamScore}`);
        }
      }
      if (hasInvalidScores) break;
    }

    if (hasInvalidScores) {
      setModalMessage(invalidMessage);
      setModalType('error');
      setShowModal(true);
      return;
    }

    try {
      setShowModal(true);
      setModalMessage('Đang lưu bảng điểm...');
      setModalType('loading');

      const result = await updateTranscript(classId, transcript);
      if (result.success) {
        setModalMessage('Lưu Điểm Thành Công');
        setModalType('success');
        setIsEditing(false);
        setValidationMessage('');
      } else {
        setModalMessage('Lỗi khi cập nhật bảng điểm: ' + (result.message || 'Unknown error'));
        setModalType('error');
      }
    } catch (error) {
      console.error('Error saving transcript:', error);
      const errorMessage = error.message || 'Lỗi không xác định';
      setModalMessage(`Lỗi khi lưu bảng điểm: ${errorMessage}`);
      setModalType('error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleBack = () => {
    navigate(`/main/class-list/${blockId}/${classId}`);
  };

  const handleLockToggle = async () => {
    if (!isAdmin) {
      setModalMessage('Bạn không có quyền khóa/mở khóa bảng điểm');
      setModalType('error');
      setShowModal(true);
      return;
    }

    if (isEditing) {
      setModalMessage('Vui lòng lưu hoặc hủy thay đổi trước khi khóa/mở khóa.');
      setModalType('warning');
      setShowModal(true);
      return;
    }

    try {
      setShowModal(true);
      setModalMessage(`Đang ${isLocked ? 'mở khóa' : 'khóa'} bảng điểm...`);
      setModalType('loading');

      const newLockStatus = !isLocked;
      await updateTranscriptLockStatus(classId, newLockStatus);
      setIsLocked(newLockStatus);
      setModalMessage(`Bảng điểm đã ${newLockStatus ? 'khóa' : 'mở khóa'}.`);
      setModalType('success');
      if (newLockStatus) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error toggling transcript lock:', error);
      setModalMessage('Lỗi khi thay đổi trạng thái khóa bảng điểm: ' + error.message);
      setModalType('error');
    }
  };

  const downloadExcel = () => {
    const data = [];
    const header = [
      'ID', 'Tên Thánh', 'Họ và Tên',
      'Học kỳ I - Miệng', 'Học kỳ I - 15 phút', 'Học kỳ I - 1 tiết', 'Học kỳ I - Thi', 'Học kỳ I - TB', 'Học kỳ I - C. Cần',
      'Học kỳ II - Miệng', 'Học kỳ II - 15 phút', 'Học kỳ II - 1 tiết', 'Học kỳ II - Thi', 'Học kỳ II - TB', 'Học kỳ II - C. Cần',
      'Cả năm - TBCN', 'Cả năm - C. Cần', 'Cả năm - XH', 'Cả năm - Thi vấn đáp'
    ];
    data.push(header);

    students.forEach(student => {
      const studentTranscript = transcript[student.id] || {};
      const row = [
        student.id, student.holyName, student.fullName,
        studentTranscript.semester1?.oral || '', studentTranscript.semester1?.fifteenMinutes || '', studentTranscript.semester1?.onePeriod || '', studentTranscript.semester1?.exam || '', studentTranscript.semester1?.average || '', studentTranscript.semester1?.diligence || '',
        studentTranscript.semester2?.oral || '', studentTranscript.semester2?.fifteenMinutes || '', studentTranscript.semester2?.onePeriod || '', studentTranscript.semester2?.exam || '', studentTranscript.semester2?.average || '', studentTranscript.semester2?.diligence || '',
        studentTranscript.yearly?.average || '', studentTranscript.yearly?.diligence || '', studentTranscript.yearly?.rank || '', studentTranscript.yearly?.oralExam || ''
      ];
      data.push(row);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Bảng điểm");
    XLSX.writeFile(wb, `BangDiem_${displayClass}.xlsx`);
  };

  if (loading) {
    return <div>Đang tải dữ liệu...</div>;
  }

  if (!transcript || Object.keys(transcript).length === 0 && students.length > 0) {
    if (!loading && students.length > 0) {
      console.warn("Transcript object empty despite having students. Re-initializing.");
      return <div>Đang chuẩn bị bảng điểm... Vui lòng đợi hoặc tải lại trang.</div>;
    }
  }

  return (
    <div className="class-transcript">
      <h3>Bảng điểm lớp: {displayClass}</h3>

      {isEditing && (
        <p className="decimal-instruction">
          Lưu ý: Nhập điểm dùng dấu chấm (.) cho phần thập phân (ví dụ: 9.5). Dấu phẩy (,) sẽ được tự động đổi thành dấu chấm.
        </p>
      )}
      {validationMessage && (
        <p className="validation-message error">{validationMessage}</p>
      )}

      {students.length > 0 ? (
        <div>
          <div className="controls-container">
            <div className="lock-toggle-container">
              <label className="lock-toggle-label">
                <div className="lock-toggle-switch">
                  <input
                    type="checkbox"
                    checked={isLocked}
                    onChange={handleLockToggle}
                    disabled={!isAdmin || isEditing}
                    title={isAdmin ? (isLocked ? "Nhấn để mở khóa" : "Nhấn để khóa") : "Chỉ admin có thể khóa/mở"}
                  />
                  <span className="lock-toggle-slider"></span>
                </div>
                <span className="lock-toggle-text">
                  {isLocked ? 'Bảng điểm đã khóa' : 'Bảng điểm đã mở'}
                </span>
              </label>
            </div>
            <div className="button-group main-buttons">
              {isEditing ? (
                <>
                  <button className="save-button" onClick={handleSave}>
                    Lưu
                  </button>
                  <button className="recalculate-button" onClick={handleRecalculate}>
                    Tính lại điểm
                  </button>
                  <button className="cancel-button" onClick={handleCancel}>
                    Hủy
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`edit-button ${isLocked ? 'disabled' : ''}`}
                    onClick={handleEdit}
                    disabled={isLocked}
                    title={isLocked ? "Mở khóa để sửa" : "Sửa bảng điểm"}
                  >
                    Sửa bảng điểm
                  </button>
                  <button className="back-button" onClick={handleBack}>
                    Quay lại
                  </button>
                  <button className="download-button" onClick={downloadExcel}>
                    Tải về file Excel
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="transcript-table">
              <thead>
                <tr>
                  <th rowSpan="2">ID</th>
                  <th rowSpan="2">Tên Thánh</th>
                  <th rowSpan="2">Họ và Tên</th>
                  <th colSpan="6">Học kỳ I</th>
                  <th colSpan="6">Học kỳ II</th>
                  <th colSpan="4">Cả năm</th>
                </tr>
                <tr>
                  <th>Miệng</th>
                  <th>15 phút</th>
                  <th>1 tiết</th>
                  <th>Thi</th>
                  <th>TB</th>
                  <th>C. Cần</th>
                  <th>Miệng</th>
                  <th>15 phút</th>
                  <th>1 tiết</th>
                  <th>Thi</th>
                  <th>TB</th>
                  <th>C. Cần</th>
                  <th>TBCN</th>
                  <th>C. Cần</th>
                  <th>XH</th>
                  <th>Thi vấn đáp</th>
                </tr>
              </thead>
              <tbody>
                {transcript && Object.keys(transcript).length > 0 && students.map((student) => {
                  const studentTranscript = transcript[student.id] || initializeTranscript({})[student.id];

                  return (
                    <tr key={student.id}>
                      <td>{student.id}</td>
                      <td>{student.holyName}</td>
                      <td>{student.fullName}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.oral || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester1', 'oral', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester1?.oral === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester1?.oral || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.fifteenMinutes || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester1', 'fifteenMinutes', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester1?.fifteenMinutes === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester1?.fifteenMinutes || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.onePeriod || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester1', 'onePeriod', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester1?.onePeriod === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester1?.onePeriod || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.exam || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester1', 'exam', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester1?.exam === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester1?.exam || '-'
                        )}
                      </td>
                      <td className="average-cell">{studentTranscript.semester1?.average || '-'}</td>
                      <td>
                        {isEditing ? (
                          <select
                            value={studentTranscript.semester1?.diligence || ''}
                            onChange={(e) =>
                              handleDiligenceChange(student.id, 'semester1', e.target.value)
                            }
                            className={`diligence-select ${studentTranscript.semester1?.diligence === '' ? 'empty-field' : ''}`}
                          >
                            <option value="">Chọn</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">Trung bình</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (
                          studentTranscript.semester1?.diligence || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.oral || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester2', 'oral', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester2?.oral === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester2?.oral || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.fifteenMinutes || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester2', 'fifteenMinutes', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester2?.fifteenMinutes === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester2?.fifteenMinutes || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.onePeriod || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester2', 'onePeriod', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester2?.onePeriod === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester2?.onePeriod || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.exam || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'semester2', 'exam', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.semester2?.exam === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.semester2?.exam || '-'
                        )}
                      </td>
                      <td className="average-cell">{studentTranscript.semester2?.average || '-'}</td>
                      <td>
                        {isEditing ? (
                          <select
                            value={studentTranscript.semester2?.diligence || ''}
                            onChange={(e) =>
                              handleDiligenceChange(student.id, 'semester2', e.target.value)
                            }
                            className={`diligence-select ${studentTranscript.semester2?.diligence === '' ? 'empty-field' : ''}`}
                          >
                            <option value="">Chọn</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">Trung bình</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (
                          studentTranscript.semester2?.diligence || '-'
                        )}
                      </td>
                      <td className="average-cell">{studentTranscript.yearly?.average || '-'}</td>
                      <td>
                        {isEditing ? (
                          <select
                            value={studentTranscript.yearly?.diligence || ''}
                            onChange={(e) =>
                              handleDiligenceChange(student.id, 'yearly', e.target.value)
                            }
                            className={`diligence-select ${studentTranscript.yearly?.diligence === '' ? 'empty-field' : ''}`}
                          >
                            <option value="">Chọn</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">Trung bình</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (
                          studentTranscript.yearly?.diligence || '-'
                        )}
                      </td>
                      <td className="rank-cell">{studentTranscript.yearly?.rank || '-'}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.yearly?.oralExam || ''}
                            onChange={(e) =>
                              handleScoreChange(student.id, 'yearly', 'oralExam', e.target.value)
                            }
                            placeholder="0-10"
                            className={`score-input ${studentTranscript.yearly?.oralExam === '' ? 'empty-field' : ''}`}
                            maxLength="4"
                          />
                        ) : (
                          studentTranscript.yearly?.oralExam || '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p>Chưa có dữ liệu học sinh cho lớp {displayClass}.</p>
      )}

      {showModal && (
        <div className={`notification-popup ${modalType}`}>
          <div className="notification-content">
            {modalType === 'loading' && <div className="spinner"></div>}
            <p>{modalMessage}</p>
            {modalType !== 'loading' && (
              <button onClick={handleCloseModal} className="confirm-button">
                Xác nhận
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTranscript;